import { computed, defineComponent, ref, watch, } from '@vue/composition-api';
import { BTN_THEMES } from '@/store/modules/ui';
import { QuestionType, SURVEY_ANSWER_SEPARATOR, } from '@/api/v2/requests/surveys';
import Modal from '@/components/Modal/Modal.vue';
import { Btn } from '@/components/Btn';
import SurveyQuestion from './SurveyQuestion.vue';
import { useForm } from '@/composables';
import { submitSurveyAnswers } from '@/api/v2/requests/surveys';
import cloneDeep from 'lodash.clonedeep';
import { useAuthUser } from '@/composables';
import LoadingSpinner from '@/components/Loaders/LoadingSpinner.vue';
import Vue from 'vue';
import { useSurveyDataCache } from '@/composables/use-survey-data-cache';
export default defineComponent({
    name: 'SurveyModal',
    components: {
        Modal,
        SurveyQuestion,
        Btn,
        LoadingSpinner,
    },
    props: {
        survey: {
            type: Object,
            required: true,
        },
        place: {
            type: Object,
            default: null,
        },
        totalCount: Number,
        show: Boolean,
        readonly: {
            type: Boolean,
            default: false,
        },
        onSuccess: {
            type: Function,
            default: null,
        },
    },
    setup(props, ctx) {
        const { authUserId } = useAuthUser();
        const { surveyCache, setRecord: setSurveyCache, deleteRecord: deleteSurveyCache, } = useSurveyDataCache(props.survey.id);
        const handleFileNameMapping = (mapping) => {
            if (!props.readonly) {
                setSurveyCache({
                    ...surveyCache.value,
                    fileNameMappings: {
                        ...surveyCache.value.fileNameMappings,
                        ...mapping,
                    },
                });
            }
        };
        const referenceForm = ref(cloneDeep({
            id: props.survey.id,
            questions: props.survey.questions,
        }));
        const { form, hasUnsavedChanges } = useForm(referenceForm);
        if (!props.readonly) {
            surveyCache.value.answers.forEach((answer, idx) => {
                form.value.questions[idx].value = answer;
                form.value.questions[idx].attachmentFile = surveyCache.value.files[idx];
                form.value.questions[idx].comment = surveyCache.value.comments[idx];
            });
        }
        const getAnswer = (question) => {
            if (question.type === QuestionType.CHECKBOX) {
                return question
                    .options.filter((option) => option.isSelected)
                    .map((option) => option.label)
                    .join(SURVEY_ANSWER_SEPARATOR);
            }
            else {
                return question.value;
            }
        };
        const parseAnswers = () => {
            const files = [];
            form.value.questions.forEach((question) => {
                files.push(question?.attachmentFile || '');
            });
            const answers = form.value.questions.map(getAnswer);
            const comments = form.value.questions.map((answer) => answer.comment || '');
            return {
                answers,
                comments,
                files,
                fileNameMappings: surveyCache.value.fileNameMappings,
            };
        };
        const questionLoadingStates = ref({});
        const isLoading = computed(() => Object.values(questionLoadingStates.value).some(Boolean));
        watch(form, () => {
            if (!props.readonly) {
                setSurveyCache(parseAnswers());
            }
        }, { deep: true });
        const updateShow = () => {
            ctx.emit('close');
        };
        const handleSubmit = async (e) => {
            if (props.readonly) {
                return;
            }
            e.preventDefault();
            const { answers, comments, files } = parseAnswers();
            const areRequiredFieldsFilled = !answers.some((answer, idx) => form.value.questions[idx].isRequired && !answer);
            const areRequredFilesUploaded = !files.some((file, idx) => form.value.questions[idx].isQuestionWithFile &&
                form.value.questions[idx].isQuestionFileRequired &&
                !file);
            if (!areRequiredFieldsFilled || !areRequredFilesUploaded) {
                ctx.root.$toast.error(ctx.root.$t('surveys.survey-required-fields-error'));
                return;
            }
            if (ctx.listeners.save) {
                ctx.emit('save', form.value.id);
            }
            try {
                await submitSurveyAnswers(form.value.id, answers, files, comments, authUserId.value, props.place?.id || '');
                deleteSurveyCache();
                ctx.root.$toast.success(ctx.root.$t('surveys.success'));
                if (props.onSuccess) {
                    props.onSuccess();
                }
            }
            catch {
                ctx.root.$toast.error(ctx.root.$t('surveys.error'));
            }
        };
        return {
            form,
            hasUnsavedChanges,
            BTN_THEMES,
            getAnswer,
            updateShow,
            surveyCache,
            handleSubmit,
            isLoading,
            questionLoadingStates,
            handleFileNameMapping,
            handleChangeQuestionLoading: ({ id, value, }) => {
                Vue.set(questionLoadingStates.value, id.value, value);
            },
        };
    },
});
